import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { useBlog } from '../context/BlogContext';
import { useUser } from '../context/UserContext';
import { useBlogModal } from '../context/BlogModalContext';
import { useThemeContext } from '../context/ThemeContext';

/**
 * Header layout goals:
 * - Left side: blog image + title + description
 * - Right side: signed-in badge + action buttons (stacked)
 *
 * Using a dedicated RightColumn prevents flex-wrap from placing the badge
 * in awkward positions (e.g., between content and buttons).
 */

const Wrapper = styled.header`
  background-color: ${({ theme }) => theme.primary || '#2cc2d4'};
  color: ${({ theme }) => theme.text || 'black'};
  padding: 20px 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  /* Allow wrapping on small screens, but keep columns coherent */
  flex-wrap: wrap;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1 1 520px;
  min-width: 320px;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  flex: 0 0 auto;
`;

const Image = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  flex: 0 0 auto;
`;

const TextContent = styled.div`
  max-width: 760px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2.6rem;
  font-family: ${({ theme }) => theme.font || 'inherit'};
`;

const Description = styled.div`
  margin-top: 10px;
  font-size: 1rem;
  color: ${({ theme }) => theme.text || 'black'};

  blockquote {
    font-style: italic;
    font-size: 1.2rem;
    border-left: 4px solid ${({ theme }) => theme.accent || '#ffcc00'};
    padding-left: 16px;
    margin: 16px 0 4px 0;
  }

  cite {
    display: block;
    text-align: right;
    font-weight: 600;
    margin-top: 4px;
  }

  p {
    margin-top: 10px;
    text-align: center;
  }
`;

const UserBadge = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-weight: 700;
  opacity: 0.95;

  /* Keep it visually subtle */
  padding: 6px 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.18);
`;

const UserLabel = styled.span`
  opacity: 0.75;
  font-weight: 700;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Button = styled.button`
  padding: 10px 18px;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ theme, $variant }) =>
    $variant === 'danger'
      ? (theme.accent || '#FFC300')
      : (theme.secondary || '#007bff')};
  color: ${({ theme }) => theme.buttonText || 'black'};
  font-family: ${({ theme }) => theme.font || 'inherit'};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme, $variant }) =>
      $variant === 'danger'
        ? '#e64a19'
        : (theme.secondaryHover || '#0056b3')};
  }
`;

/**
 * Try to produce the friendliest name we can.
 * Your UserContext fetches /api/users/me/ which usually has richer fields.
 */
function resolveDisplayName(u) {
  if (!u) return '';

  // Common fields depending on your backend serializer choices
  const preferred = [
    u.display_name,
    u.full_name,
    u.name,
  ];

  for (const v of preferred) {
    const s = (v || '').trim();
    if (s) return s;
  }

  // Try first + last
  const first = (u.first_name || '').trim();
  const last = (u.last_name || '').trim();
  const full = `${first} ${last}`.trim();
  if (full) return full;

  // Fallback
  return (u.username || '').trim();
}

function Header({ customButtons = null, hidePlatformAdmin = false }) {
  const navigate = useNavigate();
  const { openBlogSelectModal } = useBlogModal();
  const { theme } = useThemeContext();

  const { blog, selectBlog } = useBlog();
  const { blogs, user: profileUser } = useUser();

  // AuthContext is still the source of permissions + logout
  const {
    logout,
    can,
    isPlatformAdmin,
    refreshBlogAuthz,
    user: authUser,
  } = useAuth();

  const canSwitch = (blogs?.length || 0) > 1;
  const blogAdmin = can('blog:manage_settings');

  // Prefer profile user (UserContext), fallback to auth user.
  const displayName = useMemo(() => {
    return resolveDisplayName(profileUser) || resolveDisplayName(authUser);
  }, [profileUser, authUser]);

  return (
    <Wrapper theme={theme}>
      {/* LEFT COLUMN */}
      <Left>
        {blog?.header_image_url && <Image src={blog.header_image_url} alt="Blog Header" />}
        <TextContent>
          <Title theme={theme}>{blog?.title || 'TravelBlogger'}</Title>
          {blog?.description && (
            <Description theme={theme} dangerouslySetInnerHTML={{ __html: blog.description }} />
          )}
        </TextContent>
      </Left>

      {/* RIGHT COLUMN */}
      <Right>
        {displayName && (
          <UserBadge>
            <UserLabel>Signed in as</UserLabel>
            <span>{displayName}</span>
          </UserBadge>
        )}

        <ButtonGroup>
          {customButtons}

          <Button theme={theme} type="button" onClick={() => navigate('/profile')}>
            Profile
          </Button>

          <Button theme={theme} $variant="danger" type="button" onClick={logout}>
            Logout
          </Button>

          {canSwitch && (
            <Button
              theme={theme}
              type="button"
              onClick={() =>
                openBlogSelectModal(async (selectedBlog) => {
                  selectBlog(selectedBlog);
                  await refreshBlogAuthz();
                  navigate('/dashboard');
                })
              }
            >
              Switch Blog
            </Button>
          )}

          {blogAdmin && (
            <Button theme={theme} type="button" onClick={() => navigate('/blog-admin')}>
              Admin Dashboard
            </Button>
          )}

          {!hidePlatformAdmin && isPlatformAdmin && (
            <Button theme={theme} type="button" onClick={() => navigate('/platform-admin')}>
              Platform Admin
            </Button>
          )}
        </ButtonGroup>
      </Right>
    </Wrapper>
  );
}

export default Header;
